/**
 * Hook for fetching generation lock data for admin debugging
 */

import { useAuth } from '@contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export interface GenerationLock {
  userId: string;
  requestId: string;
  ageMs: number;
  isStale: boolean;
  ageMinutes: number;
}

export interface GenerationLocksData {
  activeLocksCount: number;
  staleLocks: number;
  locks: GenerationLock[];
  staleThresholdMs: number;
  timestamp: string;
}

export interface UseGenerationLocksResult {
  locks: GenerationLocksData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useGenerationLocks(): UseGenerationLocksResult {
  const { authFetch } = useAuth();
  const [locks, setLocks] = useState<GenerationLocksData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLocks = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(apiUrl('/api/admin/locks'));

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Unauthorized - admin access required');
        }
        throw new Error('Failed to fetch generation locks');
      }

      const data: GenerationLocksData = await response.json();
      setLocks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchLocks();
  }, [fetchLocks]);

  return {
    locks,
    loading,
    error,
    refetch: fetchLocks,
  };
}
